import { Preferences } from '@capacitor/preferences';
import { isNative } from './platform';

/**
 * The last profile `/auth/me` returned, kept so the app can open in a dead zone.
 *
 * Written only from a response the server actually gave us, read only when the
 * server cannot be reached. It is not a second source of truth: every online
 * boot overwrites it, and it is cleared whenever the token it describes stops
 * being the current one (see `setToken`) or the session ends (`removeToken`).
 */
export interface CachedSession {
  user: any;
  company: any | null;
  /** When the profile was last confirmed by the server. ISO 8601. */
  savedAt: string;
}

const TOKEN_KEY = 'fieldiq_token';

/**
 * Exported so `tenant-cleanup` can list it among the tenant-scoped keys without
 * spelling it a second time. A key written here and purged by a string typed
 * over there is how a previous tenant's profile survives a logout.
 */
export const SESSION_KEY = 'fieldiq_session';

/**
 * How long a cached profile may be used before the app insists on the server.
 *
 * Seven days, because that is the session JWT's own lifetime
 * (`fieldiq-api/src/routes/auth.routes.ts` — `jwt.sign(..., { expiresIn: '7d' })`,
 * with no refresh and no rotation). A profile older than that describes a token
 * that CANNOT still be valid, so honouring it would let a hand work offline for
 * a fortnight producing writes the server will reject the moment they surface.
 * Refusing it costs a sign-in; honouring it costs the work.
 */
export const CACHED_SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** True when the cached profile is young enough that its token could still be live. */
export function cachedSessionIsFresh(
  session: CachedSession,
  now: number = Date.now()
): boolean {
  const savedAt = Date.parse(session.savedAt);
  if (!Number.isFinite(savedAt)) return false;
  // A clock that has gone backwards (a device that just picked up NTP) reads as
  // a future timestamp. Not evidence of staleness — clamp rather than refuse.
  return now - savedAt < CACHED_SESSION_MAX_AGE_MS;
}

export const storage = {
  async getToken(): Promise<string | null> {
    if (isNative) {
      const { value } = await Preferences.get({ key: TOKEN_KEY });
      return value;
    }
    return localStorage.getItem(TOKEN_KEY);
  },
  async setToken(token: string): Promise<void> {
    // The cached profile describes the token that was current when it was
    // written. A NEW token invalidates it, and there is more than one door in:
    // login, InviteSetupScreen and ResetPasswordScreen all set a token, and
    // only login goes on to write a profile. Clearing here rather than at each
    // of those three is what keeps a fourth door from reintroducing the drift.
    await storage.removeCachedSession();
    if (isNative) {
      await Preferences.set({ key: TOKEN_KEY, value: token });
    }
    localStorage.setItem(TOKEN_KEY, token);
  },
  async removeToken(): Promise<void> {
    if (isNative) {
      await Preferences.remove({ key: TOKEN_KEY });
    }
    localStorage.removeItem(TOKEN_KEY);
  },

  /**
   * The cached profile, or null when there is none, it cannot be read, or it is
   * older than the token it describes could possibly be.
   *
   * Never throws. It is called on the boot path, and a corrupt value here must
   * cost the user a sign-in, not the app's ability to start.
   */
  async getCachedSession(): Promise<CachedSession | null> {
    try {
      const raw = isNative
        ? (await Preferences.get({ key: SESSION_KEY })).value
        : localStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // `user` is what the whole session hangs off — `isAuthenticated` is
      // `!!user` and every role check reads `user.role`. A record without one
      // is not a usable session, whatever else it carries.
      if (!parsed || typeof parsed !== 'object' || !parsed.user) return null;
      const session: CachedSession = {
        user: parsed.user,
        company: parsed.company ?? null,
        savedAt: String(parsed.savedAt ?? ''),
      };
      return cachedSessionIsFresh(session) ? session : null;
    } catch {
      return null;
    }
  },

  async setCachedSession(user: any, company: any | null): Promise<void> {
    if (!user) return;
    const value = JSON.stringify({
      user,
      company: company ?? null,
      savedAt: new Date().toISOString(),
    } satisfies CachedSession);
    try {
      if (isNative) {
        await Preferences.set({ key: SESSION_KEY, value });
      }
      localStorage.setItem(SESSION_KEY, value);
    } catch {
      // Storage full or blocked. The session still works for this run; the
      // device just cannot open offline next time. Not worth failing a login.
    }
  },

  async removeCachedSession(): Promise<void> {
    try {
      if (isNative) {
        await Preferences.remove({ key: SESSION_KEY });
      }
      localStorage.removeItem(SESSION_KEY);
    } catch {
      /* nothing to remove, or storage is blocked */
    }
  },
};
